import { useEffect, useState } from 'react';
import { isEqual } from 'lodash-es';
import {
  AgxColumn,
  AgxRow,
  AgxBodyText,
  Images,
  AgxFileUpload,
  SellersDisclosureUploadModel,
  AgxControlledCheckbox,
  EditablePdfButtonGroup,
  CustomPdfScript,
  UploadedDocument,
} from '@urbanx/agx-ui-components';
import { AgxButton, useDialog } from '@urbanx/agx.ui-components2';
import { isDirtyEqual } from 'utils/compares';
import { FormValue } from 'types/formConfig';

import SellerDisclosureModal, { SellerDisclosureModalValues } from './SellerDisclosureModal';

interface Props {
  id: string;
  campaignId: string;
  buttonGroups: EditablePdfButtonGroup[];
  customScript?: CustomPdfScript;
  onChange: (change: { id: string; value: SellersDisclosureUploadModel }) => void;
  defaultValue?: SellersDisclosureUploadModel;
  getAuthToken: () => Promise<string | void>;
  uploadFile?: (...args: any) => Promise<any>;
  deleteFile?: (authToken: string, filePath: string) => Promise<number | undefined>;
  getFileLink?: (authToken: string, filePath: string) => Promise<any>;
  requiredPdfFields?: string[];
  required: boolean;
  validate: boolean;
  formValues: FormValue;
  onModalComplete: (values: SellerDisclosureModalValues) => Promise<void>;
}

const SellersDisclosureUpload = ({
  id,
  defaultValue,
  getAuthToken,
  campaignId,
  buttonGroups,
  customScript,
  onChange,
  uploadFile,
  deleteFile,
  getFileLink,
  requiredPdfFields,
  required,
  validate,
  formValues,
  onModalComplete,
}: Props) => {
  const [markAsExempt, setMarkAsExempt] = useState(defaultValue?.markAsExempt ?? false);
  const [sellersDisclosureDocuments, setSellersDisclosureDocuments] = useState(
    defaultValue?.sellersDisclosureDocuments
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const [pendingFilePaths, setPendingFilePaths] = useState<string[]>([]);
  const [removalError, setRemovalError] = useState(false);
  const disclosureDialog = useDialog(SellerDisclosureModal, {
    campaignId,
    buttonGroups,
    customScript,
    requiredPdfFields,
    getAuthToken,
    getFileLink,
    uploadFile,
    deleteFile,
  });

  useEffect(() => {
    const nextMarkAsExempt = defaultValue?.markAsExempt ?? false;
    const nextDocuments = defaultValue?.sellersDisclosureDocuments;

    setMarkAsExempt((prev) => (prev === nextMarkAsExempt ? prev : nextMarkAsExempt));
    setSellersDisclosureDocuments((prev) => (isEqual(prev, nextDocuments) ? prev : nextDocuments));
  }, [defaultValue]);

  const emitChange = (nextValue: SellersDisclosureUploadModel) => {
    if (isDirtyEqual(defaultValue, nextValue)) {
      return;
    }

    onChange({
      id,
      value: nextValue,
    });
  };

  const onMarkAsExemptChanged = (value: boolean) => {
    setMarkAsExempt(value);

    emitChange({
      markAsExempt: value,
      allVendorsOrSolicitorSigned: defaultValue?.allVendorsOrSolicitorSigned ?? false,
      sellersDisclosureDocuments: sellersDisclosureDocuments,
    });
  };

  const getModalValues = (): SellerDisclosureModalValues => ({
    sellersDisclosure: {
      sellersDisclosureDocuments,
      markAsExempt,
      allVendorsOrSolicitorSigned: defaultValue?.allVendorsOrSolicitorSigned ?? false,
    },
    allVendorsOrSolicitorSigned: defaultValue?.allVendorsOrSolicitorSigned ?? false,
    surveyPlan: formValues.SurveyPlan as SellerDisclosureModalValues['surveyPlan'],
    poolSafetyFileType:
      formValues.PoolSafetyFileType as SellerDisclosureModalValues['poolSafetyFileType'],
    poolSafetyAttachedToSellerDisclosure: formValues.PoolSafetyAttachedToSellerDisclosure as
      | boolean
      | undefined,
    poolSafetyCertificatePDF:
      formValues.PoolSafetyCertificatePDF as SellerDisclosureModalValues['poolSafetyCertificatePDF'],
    buildingCertificatePDF:
      formValues.BuildingCertificatePDF as SellerDisclosureModalValues['buildingCertificatePDF'],
    poolSafetyForm36PDF:
      formValues.PoolSafetyForm36PDF as SellerDisclosureModalValues['poolSafetyForm36PDF'],
    communityTitlesSchemeDetails:
      formValues.CommunityTitlesSchemeDetails as SellerDisclosureModalValues['communityTitlesSchemeDetails'],
    bodyCorporateDetails:
      formValues.BodyCorporateDetails as SellerDisclosureModalValues['bodyCorporateDetails'],
  });

  const openDisclosureDialog = () => {
    disclosureDialog.open({
      initialValues: getModalValues(),
      onComplete: async (values) => {
        await onModalComplete(values);
        setSellersDisclosureDocuments(values.sellersDisclosure.sellersDisclosureDocuments);
      },
    });
  };

  const deleteStoredFiles = async (authToken: string, filePaths: string[]) => {
    for (const filePath of filePaths) {
      if ((await deleteFile?.(authToken, filePath)) !== 200) {
        throw new Error('Unable to remove Seller Disclosure PDF.');
      }
      setPendingFilePaths((paths) => paths.filter((path) => path !== filePath));
    }
  };

  const removeDocuments = async ({ value }: { value: UploadedDocument[] }) => {
    if (isRemoving) return;
    const remainingDocuments = sellersDisclosureDocuments?.filter((document) =>
      value.some((file) => file.documentId === document.uploadedDocument?.documentId)
    );
    if (isEqual(sellersDisclosureDocuments, remainingDocuments)) return;

    setIsRemoving(true);
    setRemovalError(false);
    try {
      const authToken = await getAuthToken();
      if (!authToken || !deleteFile) throw new Error('File removal is not configured.');
      const filePaths = [
        ...new Set(
          (sellersDisclosureDocuments ?? [])
            .filter((document) => !remainingDocuments?.includes(document))
            .flatMap((document) => document.uploadedDocument?.containerFilePath ?? [])
            .filter(
              (path) =>
                !remainingDocuments?.some(
                  (document) => document.uploadedDocument?.containerFilePath === path
                )
            )
        ),
      ];
      const values = getModalValues();
      // Persist the removal before deleting a PDF the saved campaign might still reference.
      await onModalComplete({
        ...values,
        sellersDisclosure: {
          ...values.sellersDisclosure,
          sellersDisclosureDocuments: remainingDocuments,
        },
      });
      setSellersDisclosureDocuments(remainingDocuments);
      setPendingFilePaths(filePaths);
      await deleteStoredFiles(authToken, filePaths);
    } catch {
      setRemovalError(true);
    } finally {
      setIsRemoving(false);
    }
  };

  const retryFileRemoval = async () => {
    setIsRemoving(true);
    setRemovalError(false);
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('File removal requires authentication.');
      await deleteStoredFiles(authToken, pendingFilePaths);
    } catch {
      setRemovalError(true);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <AgxColumn extraLargeGap>
      {sellersDisclosureDocuments?.some((document) => document.uploadedDocument) ? (
        <AgxFileUpload
          key={removalError ? 'restore-saved-files' : 'saved-files'}
          id={`sellersDisclosureDocuments-${id}`}
          fileData={sellersDisclosureDocuments
            .map((document) => document.uploadedDocument)
            .filter(Boolean)}
          extensions={['pdf']}
          getAuthToken={getAuthToken}
          getFileLink={getFileLink}
          onValueChanged={removeDocuments}
          readonly={isRemoving}
          disabled={isRemoving}
          fill
        />
      ) : null}
      {!sellersDisclosureDocuments?.some((document) => document.uploadedDocument) ? (
        <AgxButton
          id={`sellersDisclosureUploadDocument-${id}`}
          dataTestId="btn-uploadSellerDisclosure"
          text="Choose file"
          variant="hollow"
          size="medium"
          disabled={markAsExempt || isRemoving || pendingFilePaths.length > 0}
          onClick={openDisclosureDialog}
        />
      ) : null}
      {removalError ? (
        <div role="alert">
          <AgxBodyText small extraClasses="error">
            Unable to remove Seller Disclosure. Please try again.
          </AgxBodyText>
          {pendingFilePaths.length > 0 ? (
            <AgxButton
              id={`retrySellerDisclosureRemoval-${id}`}
              text="Retry file removal"
              variant="hollow"
              size="medium"
              disabled={isRemoving}
              onClick={retryFileRemoval}
            />
          ) : null}
        </div>
      ) : null}
      {validate &&
      required &&
      !markAsExempt &&
      !sellersDisclosureDocuments?.some((document) => document.uploadedDocument) ? (
        <AgxRow fill mediumGap>
          <Images.AlertCircle />
          <AgxBodyText small extraClasses="error" dataTestId={`error-${id}`}>
            Upload Seller Disclosure
          </AgxBodyText>
        </AgxRow>
      ) : null}

      <AgxControlledCheckbox
        key={`markAsExempt-${id}`}
        checked={markAsExempt}
        disabled={isRemoving}
        onValueChanged={onMarkAsExemptChanged}
        label="Mark as Exempt"
        naked
        id={id}
      />
    </AgxColumn>
  );
};

export default SellersDisclosureUpload;
